import { useCallback, useMemo, useRef, useState } from 'react'
import {
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from '@ag-grid-community/core'
import epsClientV1 from 'enrollment-platform/api/eps-v1.client'
import { PartnerEnum } from 'enrollment-platform/api/partner-benefit-graph/client'
import { QueryParams, SortModel, TasksResponse } from '../types'

interface UseBenefitIntegrationsDataReturn {
  isLoading: boolean
  error: string | null
  datasource: IServerSideDatasource
  clearError: () => void
}

const PARTNER_VALUES = new Set<string>(Object.values(PartnerEnum))

export const useBenefitIntegrationsData = ({
  attention,
  employeePublicKey,
  partnerBenefitName,
}: {
  attention?: string
  employeePublicKey?: string
  partnerBenefitName?: string
} = {}): UseBenefitIntegrationsDataReturn => {
  const [isLoading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const cursorsRef = useRef<Map<string, string>>(new Map())

  const trimmedEmployeePublicKey = employeePublicKey?.trim() || undefined
  const validPartnerBenefitName = PARTNER_VALUES.has(partnerBenefitName ?? '')
    ? partnerBenefitName
    : undefined

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const buildQueryParams = useCallback(
    (
      limit: number,
      filterModel: any,
      sortModel: SortModel[],
      filterKey: string,
      startRow: number
    ): QueryParams => {
      const queryParams: QueryParams = { limit }
      if (attention !== undefined) {
        queryParams.attention = attention
      }
      if (trimmedEmployeePublicKey) {
        queryParams.employeePublicKey = trimmedEmployeePublicKey
      }
      if (validPartnerBenefitName) {
        queryParams.partnerBenefitName = validPartnerBenefitName
      }

      if (startRow > 0) {
        const cursor = cursorsRef.current.get(filterKey)
        if (cursor) {
          queryParams.cursor = cursor
        }
      }

      if (filterModel) {
        Object.keys(filterModel).forEach((key) => {
          const filter = filterModel[key]
          switch (key) {
            case 'personFirstName':
            case 'personLastName':
            case 'clientCompanyName':
              if (filter.filter) {
                queryParams[key] = filter.filter
              }
              break
            case 'taskStatus':
              if (filter.values && filter.values.length > 0) {
                queryParams[key] = filter.values.join(',')
              }
              break
            case 'partnerSyncStatus':
              if (filter.values && filter.values.length > 0) {
                queryParams[key] = filter.values.join(',')
              }
              break
          }
        })
      }

      if (sortModel && sortModel.length > 0) {
        const sort = sortModel[0]
        queryParams.sortBy = sort.colId
        queryParams.sortOrder = sort.sort
      }

      return queryParams
    },
    [attention, trimmedEmployeePublicKey, validPartnerBenefitName]
  )

  const datasource: IServerSideDatasource = useMemo(
    () => ({
      getRows: async (params: IServerSideGetRowsParams) => {
        try {
          setLoading(true)
          setError(null)

          const { request } = params
          const { startRow, endRow, filterModel, sortModel } = request

          const limit = (endRow ?? 0) - (startRow ?? 0)
          const filterKey = JSON.stringify({
            filterModel: filterModel || {},
            pageSize: limit,
            attention,
            employeePublicKey,
            partnerBenefitName,
          })

          const queryParams = buildQueryParams(
            limit,
            filterModel,
            sortModel,
            filterKey,
            startRow ?? 0
          )

          console.log('AG Grid Request:', {
            startRow,
            endRow,
            limit,
            filterModel,
            sortModel,
          })

          const response = await epsClientV1.get<TasksResponse>(
            '/benefit-integration-tasks',
            { params: queryParams }
          )

          const { tasks, nextCursor, hasNextPage } = response.data

          if (nextCursor) {
            cursorsRef.current.set(filterKey, nextCursor)
          }

          const lastRow = hasNextPage
            ? undefined
            : (startRow ?? 0) + tasks.length

          params.success({
            rowData: tasks,
            rowCount: lastRow,
          })
        } catch (error: any) {
          console.error('Error fetching benefit integrations:', error)
          const errorMessage =
            error.response?.data?.message || 'Failed to fetch data'
          setError(errorMessage)
          params.fail()
        } finally {
          setLoading(false)
        }
      },
    }),
    [attention, employeePublicKey, partnerBenefitName, buildQueryParams]
  )

  return {
    isLoading,
    error,
    datasource,
    clearError,
  }
}
